import { useState, useEffect, useMemo } from 'react'
import { Button, Typography, Grid, Box, Card, CardContent, Alert, useMediaQuery, styled
  , Stack, Divider, TextField, FormControl, Select, MenuItem  } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { useConfirm } from '@components/ConfirmProvider'
import PhoneIdentityVerification from "@components/PhoneIdentityVerification";
import { useIsMobile } from '@/hooks/Responsive'
import { ReactComponent as Phone } from '@assets/images/icon/ico_phone.svg'

export default function Step3({ type, MemberKind, kind, terms, profile, onVerified, tempMemberInfo }) {
  const { isMobile } = useIsMobile()

  const { alert, confirm } = useConfirm();

  const [childName, setChildName] = useState("");  // 미성년자 이름
  const [childBirthYear, setChildBirthYear] = useState("");  // 미성년자 출생연도
  const [childGender, setChildGender] = useState("");  // 미성년자 성별
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [verifyBtnView, setVerifyBtnView] = useState(kind === MemberKind.G14D ? false : true);  // 휴대전화인증버튼 view여부
  const [infoLocked, setInfoLocked] = useState(false) 

  const isMinor = kind === MemberKind.G14D;  // 미성년자 여부
  const requiredOK = isMinor ? childName && childBirthYear && childGender : true;  // 버튼 disabled 여부

  useEffect(() => {
    // 본인인증[모바일] 콜백을 위한 저장(redirect시 데이터 다시 등록)
    localStorage.setItem("Step3.step", 3);  // 단계구분
    localStorage.setItem("Step3.type", type);  // 회원유형
    localStorage.setItem("Step3.kind", kind);  // 회원종류
    localStorage.setItem("Step3.terms", JSON.stringify(terms));  // 약관동의
    localStorage.setItem("Step3.profile", JSON.stringify(profile));  // 간편회원가입정보
    localStorage.setItem("Step3.tempMemberInfo", JSON.stringify(tempMemberInfo));  // 임시회원정보

    // 임시회원인 경우 14세미만은 등록된 데이터로 입력
    if (kind == MemberKind.G14D && tempMemberInfo) {
      setChildName(tempMemberInfo.name);
      setChildBirthYear(tempMemberInfo.birthDay);
      setChildGender(tempMemberInfo.sex);
    }
  }, []);

  // 입력값 검증
  const validate = () => {
    const newErrors = {};
    // 미성년자 인증인 경우
    if (isMinor) {
      if (!childName) newErrors.childName = "이름을 입력해 주세요.";
      if (!childBirthYear) newErrors.childBirthYear = "출생연도를 입력해 주세요.";
      if (!childGender) newErrors.childGender = "성별을 선택해 주세요.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;

  }

  const currentYear = new Date().getFullYear();

  // 만14세 미만: 출생연도 [currentYear-13, currentYear]
  const yearRange = { start: currentYear - 13, end: currentYear };

  const years = useMemo(() => {
    const arr = [];
    for (let y = yearRange.start; y <= yearRange.end; y++) {
      arr.push(String(y));
    }
    return arr;
  }, [yearRange.start, yearRange.end]);

  // 인증버튼 view를 위한 체크
  const verifyBtnCheck = () => {
    if (validate()) {
      // 본인인증[모바일] 콜백을 위한 저장(redirect시 데이터 다시 등록)
      // 14세 미만 회원정보
      localStorage.setItem("Step3.childName", childName);  // 미성년자 이름
      localStorage.setItem("Step3.childBirthYear", childBirthYear);  // 미성년자 출생연도
      localStorage.setItem("Step3.childGender", childGender);  // 미성년자 성별

      // 휴대전화인증버튼 view여부
      setVerifyBtnView(true);
    }
  }



  // [PC버전]PortOne인증 콜백 함수
  const doPortOne = (data) => {
    if (data?.status == 'VERIFIED') {
      if (kind == MemberKind.G14D) {
        doPortOneSubmit(data);
      }
      else {
        // 임시회원정보 체크
        if (tempMemberInfo) {
          // 임시회원정보와 일치하지 않는 경우(이름, 출생연도, 전화번호)
          if (
            tempMemberInfo.name !== data?.name ||
            tempMemberInfo.birthDay !== data?.birthDate.slice(0, 4) ||
            tempMemberInfo.phone !== data?.phone
          ) {
            alert('등록된 임시회원정보와 일치하지 않습니다.');
          }
          else {
            doPortOneSubmit(data);
          }
        }
        // 신규회원 가입인 경우
        else {
          doPortOneSubmit(data);
        }
      }
    }
    else {
      setError("본인인증을 실패했습니다.");
    }
  };

  // doPortOne return
  const doPortOneSubmit = (data) => {
    setError("");
    onVerified(
      {
        name: kind === MemberKind.G14D ? childName : data?.name,
        birthDay: kind === MemberKind.G14D ? childBirthYear : data?.birthDate.slice(0, 4),  // 앞4자리만
        gender: kind === MemberKind.G14D ? childGender : data?.gender.slice(0, 1),  // 앞1자리만
        phone: data?.phone,
        nationality: data?.foreigner == 'true' ? "FOREIGNER" : "LOCAL",
        certToken: data?.certToken,
      }
    );
  };



  return (
    <Box>
      {isMinor ? (
        <>
          <Typography component="h3" variant={isMobile ? 'h6' : 'h5'} sx={{ mb: 1.5 }}>
            법정대리인 본인인증
          </Typography>
        </>
          ) : (
        <>
          <Typography component="h3" variant={isMobile ? 'h6' : 'h5'} sx={{ mb: 1.5 }}>
            본인인증
          </Typography>
        </>
      )}
      <Typography component="p" variant={isMobile ? 'body1' : 'subtitle2'} fontWeight={400}>
        OpenVLab은 개인정보에 대한 유출 및 도용을 방지하기 위해 본인인증을 시행하고 있습니다.
      </Typography>

      {isMinor ? (
      <Typography component="p" variant={isMobile ? 'body1' : 'subtitle2'} fontWeight={400} sx={{ mb: isMobile ? 4 : 6 }}>
        만 14세 미만 아동의 개인정보 수집 시 보호자(법정대리인)의 동의를 얻도록 규정하고 있습니다. <br />
        "부모님(법적보호자)의 동의"는 <Typography component="span" variant={isMobile ? 'body1' : 'subtitle2'} fontWeight={500} color="primary.main">부모님(법적보호자)의 본인인증으로 동의</Typography> 한 것으로 간주됩니다.
      </Typography>
        ) : (
      <Typography component="p" variant={isMobile ? 'body1' : 'subtitle2'} fontWeight={400} sx={{ mb: isMobile ? 4 : 6 }}>
        <Typography component="span" variant={isMobile ? 'body1' : 'subtitle2'} fontWeight={500} color="primary.main">「정보통신망 이용촉진 및 정보보호 등에 관한 법률 제23조 2항(주민번호 사용 제한)」에 의거</Typography>하여, 웹사이트 내 주민등록번호 수집·이용이 금지되어 주민번호 입력 기반의 인증 서비스 사용이 제한됩니다.
      </Typography>
      )}

      <Box>
        {/* 14세미만(법정대리인 본인인증) */}
        {/* 14세 미만 회원 정보 입력 */}
        {isMinor ? (
          <>
            <Typography component="h4" variant={isMobile ? 'subtitle2' : 'subtitle1'} sx={{ mb: 4 }}>만 14세 미만 회원 정보 입력</Typography>
            <Box sx={{ mb: 4 }}>
              <Typography fontWeight={500} sx={{ mb: 0.5 }}>
                이름 <Typography component="span" color="error">*</Typography>
                <Box component="span" sx={visuallyHidden}>필수 입력</Box>
              </Typography>
              <TextField 
                fullWidth 
                id="name" 
                name="name" 
                value={childName} 
                sx={{ flex: 1 }} 
                inputProps={{ maxLength: 30 }}
                onChange={(e) => setChildName(e.target.value)} 
                placeholder="이름을 입력해 주세요" 
                error={!!errors.childName}
                disabled={infoLocked}
              />
              {errors.childName && (
                <Typography variant="body3" color="error.main">{errors.childName}</Typography>
              )}
            </Box>
            
            <Grid container spacing={4} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Typography fontWeight={500} sx={{ mb: 0.5 }}>
                  출생연도 <Typography component="span" color="error">*</Typography>
                  <Box component="span" sx={visuallyHidden}>필수 입력</Box>
                </Typography>
                <Select
                  id="birthYear"
                  name="birthYear"
                  value={childBirthYear}
                  onChange={(e) => setChildBirthYear(e.target.value)}
                  error={!!errors.childBirthYear}
                  displayEmpty
                  fullWidth
                  disabled={infoLocked}
                >
                  {/* placeholder */}
                  <MenuItem value="">
                    선택
                  </MenuItem>

                  {/* 동적 생성된 연도 옵션 */}
                  {years.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
                {errors.childBirthYear && (
                  <Typography variant="body3" color="error.main">{errors.childBirthYear}</Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography fontWeight={500} sx={{ mb: 0.5 }}>
                  성별 <Typography component="span" color="error">*</Typography>
                  <Box component="span" sx={visuallyHidden}>필수 입력</Box>
                </Typography>
                <Select
                  id="gender"
                  name="gender"
                  value={childGender}
                  onChange={(e) => setChildGender(e.target.value)}
                  error={!!errors.childGender}
                  displayEmpty
                  fullWidth
                  disabled={infoLocked}
                >
                  {/* placeholder */}
                  <MenuItem value="">
                    선택
                  </MenuItem>

                  <MenuItem value="M">남자</MenuItem>
                  <MenuItem value="F">여자</MenuItem>
                </Select>
                {errors.childGender && (
                  <Typography variant="body3" color="error.main">{errors.childGender}</Typography>
                )}
              </Grid>
            </Grid>

            <Button 
              variant="contained" 
              color="primary" 
              size="large" 
              sx={{ width: '100%' }} 
              onClick={() => { 
                verifyBtnCheck(); 
                if (requiredOK) setInfoLocked(true)
              }}
              disabled={infoLocked}
            >
              확인
            </Button>

            {infoLocked && (
              <Box sx={{ mt: 8 }}>
                <Typography component="h4" variant={isMobile ? 'subtitle2' : 'subtitle1'} sx={{ mb: 1.5 }}>법정대리인 휴대전화 인증</Typography>
                <Typography component="p" sx={{ mb: 4 }}>보호자 명의의 휴대전화로 인증을 진행해 주세요.</Typography>
                
                <PhoneIdentityVerification
                  onSuccess={doPortOne}
                  disabled={!(verifyBtnView && requiredOK)}
                  mode="signup"
                  fullWidth
                />

              </Box>
            )}
          </>
        ) : 
        <>
          {/* 휴대전화 인증 */}
          <Card
            variant="outlined"
            sx={{
              borderRadius: 3,
              mb: 1.5,
            }}
          >
            <CardContent
              sx={{
                minHeight: '136px',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 3,
                gap: 3,
              }}
            >
              <Stack direction={isMobile ? 'column' : 'row'} alignItems="center" spacing={1}>
                <Box component={Phone} />
                <Typography variant="subtitle2" fontWeight={500}>
                  휴대전화 번호로 본인 인증
                </Typography>
              </Stack>
              
              <PhoneIdentityVerification
                onSuccess={doPortOne}
                disabled={!(verifyBtnView && requiredOK)}
                mode="signup"
              />

            </CardContent>
          </Card>
        </>
        }
      </Box>

      {isMinor ? (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          만 14세 미만은 개인정보 수집 시 보호자의 동의가 필요합니다. 법정대리인의 본인 인증을 통해 가입을 진행해 주세요.
        </Alert>
      ) : (
        <Alert severity="info">
          본인 명의의 휴대전화로 인증을 할 수 없는 경우 회원가입에 제한이 있습니다.
        </Alert>
      )}

    </Box>
  )
}